import { api } from "./api";

export type PushStatus = "unsupported" | "disabled" | "prompt" | "subscribed" | "denied";

type PushConfig = { enabled: boolean; publicKey: string | null };
type PreparedPush = {
  config: PushConfig;
  registration: ServiceWorkerRegistration | null;
  subscription: PushSubscription | null;
};

const PREPARATION_TIMEOUT_MS = 10_000;
const PERMISSION_TIMEOUT_MS = 15_000;
const SUBSCRIPTION_TIMEOUT_MS = 20_000;
const SERVER_TIMEOUT_MS = 12_000;

let preparedPush: PreparedPush | null = null;
let preparationPromise: Promise<PreparedPush> | null = null;
let reconciliationPromise: Promise<PushSubscription | null> | null = null;

function decodeVapidKey(value: string) {
  const padding = "=".repeat((4 - value.length % 4) % 4);
  const raw = atob((value + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((character) => character.charCodeAt(0)));
}

function encodeSubscriptionKey(value: ArrayBuffer | null) {
  if (!value) return "";
  let binary = "";
  for (const byte of new Uint8Array(value)) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, message: string) {
  return new Promise<T>((resolve, reject) => {
    const timer = globalThis.setTimeout(() => reject(new Error(message)), timeoutMs);
    promise.then(
      (value) => { globalThis.clearTimeout(timer); resolve(value); },
      (error) => { globalThis.clearTimeout(timer); reject(error); },
    );
  });
}

async function apiWithTimeout<T>(path: string, init: RequestInit = {}) {
  const controller = new AbortController();
  const timer = globalThis.setTimeout(() => controller.abort(), SERVER_TIMEOUT_MS);
  try {
    return await api<T>(path, { ...init, signal: controller.signal });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new Error("O servidor demorou para confirmar este aparelho. Tente novamente.");
    }
    throw error;
  } finally {
    globalThis.clearTimeout(timer);
  }
}

function friendlyPushError(error: unknown) {
  if (!(error instanceof Error)) return new Error("Não foi possível ativar as notificações.");
  if (error.message.includes("demorou") || error.message.includes("Permissão") || error.message.includes("preparando")) return error;
  if (error.name === "NotAllowedError") {
    return new Error("O iPhone não autorizou as notificações. Verifique os Ajustes do aparelho e tente novamente.");
  }
  if (error.name === "AbortError") {
    return new Error("A ativação foi interrompida. Tente novamente.");
  }
  return new Error("O aparelho não concluiu a ativação. Tente novamente.");
}

function subscriptionPayload(subscription: PushSubscription, deviceLabel?: string) {
  const serialized = subscription.toJSON();
  const p256dh = serialized.keys?.p256dh || encodeSubscriptionKey(subscription.getKey("p256dh"));
  const auth = serialized.keys?.auth || encodeSubscriptionKey(subscription.getKey("auth"));
  if (!subscription.endpoint || !p256dh || !auth) {
    throw new Error("O aparelho não concluiu a ativação.");
  }
  return {
    endpoint: subscription.endpoint,
    expirationTime: serialized.expirationTime ?? null,
    keys: { p256dh, auth },
    deviceLabel: deviceLabel ?? navigator.platform ?? "Este aparelho",
  };
}

async function registerSubscription(subscription: PushSubscription, deviceLabel?: string) {
  await apiWithTimeout("/notifications/push", {
    method: "POST",
    body: JSON.stringify(subscriptionPayload(subscription, deviceLabel)),
  });
}

export function pushSupported() {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export async function preparePushNotifications(force = false): Promise<PreparedPush> {
  if (!pushSupported()) throw new Error("Este aparelho não oferece notificações push.");
  if (force) {
    preparedPush = null;
    preparationPromise = null;
  }
  if (preparedPush) return preparedPush;
  if (preparationPromise) return preparationPromise;

  preparationPromise = (async () => {
    const config = await apiWithTimeout<PushConfig>("/notifications/push/config");
    if (!config.enabled || !config.publicKey) {
      return { config, registration: null, subscription: null };
    }
    const registration = await withTimeout(
      navigator.serviceWorker.ready,
      PREPARATION_TIMEOUT_MS,
      "O aplicativo ainda está preparando as notificações. Tente novamente.",
    );
    const subscription = await withTimeout(
      registration.pushManager.getSubscription(),
      PREPARATION_TIMEOUT_MS,
      "O aparelho demorou para verificar as notificações. Tente novamente.",
    );
    return { config, registration, subscription };
  })();

  try {
    preparedPush = await preparationPromise;
    return preparedPush;
  } catch (error) {
    preparationPromise = null;
    throw error;
  }
}

export async function pushStatus(): Promise<PushStatus> {
  if (!pushSupported()) return "unsupported";
  const prepared = await preparePushNotifications();
  if (!prepared.config.enabled || !prepared.config.publicKey || !prepared.registration) return "disabled";
  if (Notification.permission === "denied") return "denied";
  if (!prepared.subscription) return "prompt";
  const status = await apiWithTimeout<{ registered: boolean }>("/notifications/push/status", {
    method: "POST",
    body: JSON.stringify({ endpoint: prepared.subscription.endpoint }),
  });
  return status.registered ? "subscribed" : "prompt";
}

export async function enablePushNotifications(deviceLabel?: string) {
  if (!pushSupported()) throw new Error("Este aparelho não oferece notificações push.");
  const prepared = preparedPush;
  if (!prepared) {
    void preparePushNotifications().catch(() => undefined);
    throw new Error("As notificações ainda estão preparando. Aguarde um instante e tente novamente.");
  }
  if (!prepared.config.enabled || !prepared.config.publicKey || !prepared.registration) {
    throw new Error("Notificações ainda não estão disponíveis.");
  }
  if (Notification.permission === "denied") {
    throw new Error("Permissão de notificações não concedida.");
  }

  try {
    let subscription = prepared.subscription;
    if (!subscription) {
      if (Notification.permission !== "granted") {
        const permission = await withTimeout(
          Notification.requestPermission(),
          PERMISSION_TIMEOUT_MS,
          "O iPhone demorou para responder à permissão. Feche o aviso do sistema e tente novamente.",
        );
        if (permission !== "granted") throw new Error("Permissão de notificações não concedida.");
      }

      // No iOS esta chamada precisa decorrer diretamente do toque, sem uma requisição de rede antes.
      subscription = await withTimeout(
        prepared.registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: decodeVapidKey(prepared.config.publicKey),
        }),
        SUBSCRIPTION_TIMEOUT_MS,
        "O iPhone demorou para concluir a ativação. Tente novamente.",
      );
    }

    await registerSubscription(subscription, deviceLabel);
    preparedPush = { ...prepared, subscription };
    return subscription;
  } catch (error) {
    void preparePushNotifications(true).catch(() => undefined);
    throw friendlyPushError(error);
  }
}

export async function reconcilePushNotifications(deviceLabel?: string) {
  if (!pushSupported() || Notification.permission !== "granted") return null;
  if (reconciliationPromise) return reconciliationPromise;

  reconciliationPromise = (async () => {
    const prepared = await preparePushNotifications();
    if (!prepared.config.enabled || !prepared.config.publicKey || !prepared.registration) return null;

    let subscription = prepared.subscription;
    if (subscription) {
      const status = await apiWithTimeout<{ registered: boolean }>("/notifications/push/status", {
        method: "POST",
        body: JSON.stringify({ endpoint: subscription.endpoint }),
      });
      if (status.registered) return subscription;

      await withTimeout(
        subscription.unsubscribe(),
        SUBSCRIPTION_TIMEOUT_MS,
        "O aparelho demorou para renovar as notificações.",
      );
      subscription = null;
    }

    subscription = await withTimeout(
      prepared.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: decodeVapidKey(prepared.config.publicKey),
      }),
      SUBSCRIPTION_TIMEOUT_MS,
      "O aparelho demorou para renovar as notificações.",
    );
    await registerSubscription(subscription, deviceLabel);
    preparedPush = { ...prepared, subscription };
    return subscription;
  })();

  try {
    return await reconciliationPromise;
  } catch (error) {
    void preparePushNotifications(true).catch(() => undefined);
    throw friendlyPushError(error);
  } finally {
    reconciliationPromise = null;
  }
}

export async function disablePushNotifications() {
  if (!pushSupported()) return;
  const prepared = await preparePushNotifications();
  const subscription = prepared.subscription;
  if (!subscription) return;
  await apiWithTimeout("/notifications/push/unsubscribe", {
    method: "POST", body: JSON.stringify({ endpoint: subscription.endpoint }),
  });
  await withTimeout(
    subscription.unsubscribe(),
    SUBSCRIPTION_TIMEOUT_MS,
    "O aparelho demorou para desativar as notificações. Tente novamente.",
  );
  preparedPush = { ...prepared, subscription: null };
}
